import { compressImageFile } from "@bto/shared";
import React, { useEffect, useRef, useState } from "react";
import Dropzone, {
  IFileWithMeta,
  IPreviewProps,
  StatusValue,
} from "react-dropzone-uploader";

import {
  FileUploaderTitle,
  NeqReqFileUploaderDropzoneInsurance,
  FileUploaderBrowse,
  FileUploaderDropzoneWrapper,
  FileUploaderWrapper,
  FileUploaderPreview,
  FileUploaderPreviewButton,
  FileUploaderPreviewTitle,
  FileUploaderPreviewDelete,
  FileUploaderPreviewError,
  FileNameUploaded,
  NewReqFileUploaderGreyText,
  NewReqFileUploaderDropzoneAntigen,
} from "./NewReqFileUploader.styles";
import "react-dropzone-uploader/dist/styles.css";
import { themePalette } from "../../theme";
import { SuccessUploadIcon } from "../../assets/iconComponents/SuccessUploadIcon";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import {
  toBase64,
  uploadNewReqImageForProcessing,
} from "./Services/NewReqService";
import { fetchOrLogout } from "../../utils/helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

export interface FileUploadInfo {
  [fileName: string]: {
    fileName: string;
    id: string;
    valid: boolean;
    file: string;
  };
}

const FeedbackFileUploadInsurance = () => (
  <NeqReqFileUploaderDropzoneInsurance key="custom">
    <FileUploaderBrowse>UPLOAD FILE</FileUploaderBrowse>
    <NewReqFileUploaderGreyText> or </NewReqFileUploaderGreyText>
    <FileUploaderTitle>Drag and drop files here</FileUploaderTitle>
  </NeqReqFileUploaderDropzoneInsurance>
);

const FeedbackFileUploadAntigen = () => (
  <NewReqFileUploaderDropzoneAntigen key="custom">
    <FileUploaderBrowse>UPLOAD FILE</FileUploaderBrowse>
    <NewReqFileUploaderGreyText>
      or drag and drop files here
    </NewReqFileUploaderGreyText>
  </NewReqFileUploaderDropzoneAntigen>
);

interface FileUploaderProps {
  type: "antigen" | "insurance";
  insuranceType?: "secondary" | "primary";
  cardType?: "front" | "back";
  value: FileUploadInfo;
  setValue: (files: FileUploadInfo) => void;
  uploadProcessing: boolean;
  preflight: boolean;
  setUploadProcessing: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewReqFileUploader: React.FC<FileUploaderProps> = ({
  value,
  type,
  setValue,
  uploadProcessing,
  setUploadProcessing,
  preflight,
  insuranceType,
  cardType,
}) => {
  const [dropzoneKey] = useState(0);
  const [activeFiles, setActiveFiles] = useState<FileUploadInfo>(value);
  const hasHydratedInitialValue = useRef(Boolean(Object.keys(value).length));
  const [initialFiles, setInitialFiles] = useState<File[]>();
  const { auth, setLoggedOut } = useAuthenticationHook();

  useEffect(() => {
    if (
      !hasHydratedInitialValue.current &&
      Object.keys(value).length &&
      !Object.keys(activeFiles).length
    ) {
      hasHydratedInitialValue.current = true;
      setActiveFiles(value);
    }
  }, [activeFiles, value]);

  useEffect(() => {
    if (!initialFiles && Object.keys(value).length) {
      const setFiles = async () => {
        const files: File[] = [];
        await Promise.all(
          Object.keys(value).map(async (fileName) => {
            const response = await fetchOrLogout(
              value[fileName].file,
              {},
              setLoggedOut,
            );
            const buffer = await response.arrayBuffer();
            const file = new File([buffer], fileName, {
              type: value[fileName].file.substring(
                "data:".length,
                value[fileName].file.indexOf(";base64"),
              ),
            });
            files.push(file);
          }),
        );
        setInitialFiles(files);
      };
      setFiles().catch((err) => {
        console.error("New req file preload error", err);
      });
    }
  }, []);

  const PreviewComponent = ({ meta, fileWithMeta }: IPreviewProps) => {
    const { name, previewUrl } = meta;
    const { remove } = fileWithMeta;

    return (
      <FileUploaderPreview
        previewUrl={previewUrl}
        validUpload={activeFiles[name]?.valid}
      >
        <FileUploaderPreviewTitle>
          {activeFiles[name]?.valid !== undefined &&
            (activeFiles[name]?.valid ? (
              <SuccessUploadIcon />
            ) : (
              <ExclamationIcon color={themePalette.redMedium} />
            ))}
          <FileNameUploaded> {name} </FileNameUploaded>
        </FileUploaderPreviewTitle>
        <FileUploaderPreviewDelete>
          <FileUploaderPreviewError>
            {activeFiles[name]?.valid === false ? "Upload Failed!" : ""}
          </FileUploaderPreviewError>
          {activeFiles[name]?.valid !== undefined && !preflight && (
            <FileUploaderPreviewButton onClick={() => remove()}>
              Remove
            </FileUploaderPreviewButton>
          )}
        </FileUploaderPreviewDelete>
      </FileUploaderPreview>
    );
  };

  useEffect(() => {
    if (!preflight) {
      setValue(activeFiles);
    }
  }, [activeFiles]);

  const handleChangeStatus: (
    recentFile: IFileWithMeta,
    status: StatusValue,
  ) => void = async (recentFile: IFileWithMeta, status: StatusValue) => {
    const activeFileName = activeFiles[recentFile.file.name]?.fileName;
    const activeID = activeFiles[recentFile.file.name]?.id;

    if (status === "done" && auth) {
      if (!activeFileName) {
        setUploadProcessing(true);
        try {
          const compressedFile = await compressImageFile(recentFile.file);
          const base64Image = await toBase64(compressedFile);
          const response = await uploadNewReqImageForProcessing(
            base64Image,
            auth.token,
            preflight,
            type === "antigen" ? "antigen" : "insurance",
            setLoggedOut,
            insuranceType,
            cardType,
          );
          if (response) {
            await setActiveFiles((active) => ({
              ...active,
              [recentFile.file.name]: {
                ...response,
                id: activeID || recentFile.meta.id,
                file: base64Image,
              },
            }));
            recentFile.restart();
            if (activeID) {
              recentFile.remove();
            }
          }
        } catch (err) {
          console.error("New req file upload error", err);
        } finally {
          setUploadProcessing(false);
        }
      } else if (activeID === recentFile.meta.id) {
        recentFile.remove();
      } else {
        await setActiveFiles((active) => ({
          ...active,
          [recentFile.file.name]: {
            ...active[recentFile.file.name],
            id: recentFile.meta.id,
          },
        }));
      }
    } else if (status === "removed") {
      const oldActive = { ...activeFiles };
      if (oldActive[recentFile.file.name]?.id === recentFile?.meta.id) {
        delete oldActive[recentFile.file.name];
        await setActiveFiles(oldActive);
      }
    }
  };

  return (
    <>
      <FileUploaderWrapper>
        <FileUploaderDropzoneWrapper preflight={preflight}>
          <Dropzone
            disabled={uploadProcessing}
            key={dropzoneKey}
            accept=".png, .jpg, .jpeg, .pdf"
            initialFiles={initialFiles?.length ? initialFiles : undefined}
            onChangeStatus={(recentFile: IFileWithMeta, status: StatusValue) =>
              handleChangeStatus(recentFile, status)
            }
            submitButtonDisabled
            styles={{
              dropzone: {
                border: "none",
                height:
                  preflight || type === "antigen" ? "fit-content" : "180px",
                overflow: "hidden",
                background: themePalette.neutral50,
                display: "flex",
                flexFlow: "wrap",
                flexDirection: "row",
                overflowY: type === "antigen" ? "hidden" : "scroll",
                alignItems: "start",
                justifyContent: "center",
              },
              submitButtonContainer: {
                display: "none",
              },
              preview: {
                backgroundColor: themePalette.redMedium,
                padding: "20px 3%",
              },
            }}
            inputContent={
              type === "antigen"
                ? FeedbackFileUploadAntigen()
                : FeedbackFileUploadInsurance()
            }
            PreviewComponent={PreviewComponent}
          />
        </FileUploaderDropzoneWrapper>
      </FileUploaderWrapper>
    </>
  );
};

export default NewReqFileUploader;
